import { useState, useEffect, useRef, useMemo } from "react";
import { FaUser, FaAngleDown, FaCalendarCheck } from "react-icons/fa6"; // Додали іконку календаря
import { useTelegram } from "../../context/TelegramContext";
import { useTranslation } from "react-i18next";
import { getProfile, postChangeAppLanguage } from "../../api/mainApi";
import { GB, UA, RU, ES, PT } from "country-flag-icons/react/3x2";

function Profile() {
  const tg = useTelegram();
  const { t, i18n } = useTranslation();
  const [achievements, setAchievements] = useState(null);
  const [regDate, setRegDate] = useState(null);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
  const menuRef = useRef(null);

  const isCisRegion = useMemo(() => {
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const cisTimezones = [
        "Europe/Moscow",
        "Europe/Minsk",
        "Europe/Volgograd",
        "Europe/Samara",
        "Asia/Yekaterinburg",
        "Asia/Omsk",
        "Asia/Novosibirsk",
        "Asia/Krasnoyarsk",
        "Asia/Irkutsk",
        "Asia/Yakutsk",
        "Asia/Vladivostok",
        "Asia/Magadan",
        "Asia/Kamchatka",
        "Asia/Almaty",
        "Asia/Tashkent",
        "Asia/Bishkek",
        "Asia/Dushanbe",
        "Asia/Yerevan",
        "Asia/Baku",
        "Asia/Ashgabat",
        "Asia/Tbilisi",
      ];

      return cisTimezones.some((zone) => userTimezone.includes(zone));
    } catch (e) {
      return false;
    }
  }, []);

  const allLanguages = [
    { code: "en", Flag: GB, alt: "English" },
    { code: "uk", Flag: UA, alt: "Українська" },
    { code: "ru", Flag: RU, alt: "Русский" },
    { code: "es", Flag: ES, alt: "Español" },
    { code: "pt", Flag: PT, alt: "Português" },
  ];

  const languages = allLanguages.filter((lang) => {
    if (lang.code === "ru") {
      return isCisRegion;
    }
    return true;
  });

  useEffect(() => {
    const supportedCodes = languages.map((l) => l.code);
    if (!supportedCodes.includes(selectedLanguage)) {
      const fallback = "en";
      setSelectedLanguage(fallback);
      i18n.changeLanguage(fallback);
    }
  }, [selectedLanguage, languages, i18n]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await getProfile();
        setAchievements(data.achievements);
        setRegDate(data.reg_date);
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsLanguageMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    const formatter = new Intl.DateTimeFormat(i18n.language, options);
    return formatter.format(date);
  };

  const handleLanguageChange = (code) => {
    postChangeAppLanguage(code);
    setSelectedLanguage(code);
    i18n.changeLanguage(code);
    localStorage.setItem("i18nextLng", code);
    setIsLanguageMenuOpen(false);
  };

  return (
    <div className="flex flex-col h-dvh bg-gray-950 text-white">
      {/* 1. ЧИСТИЙ ХЕДЕР ДЛЯ TELEGRAM (Оновлені відступи) */}
      <div className="w-full pt-11 pb-2 px-20 flex justify-center items-center shrink-0">
        <h1 className="text-xl font-semibold text-center truncate w-full">
          {t("profile")}
        </h1>
      </div>

      {/* 2. СКРОЛ-ЗОНА */}
      <div className="overflow-y-auto pb-4 flex-1 lg:custom-scrollbar relative">
        {/* КОНТЕЙНЕР ФОТОГРАФІЇ ТА МОВИ */}
        <div className="w-full bg-gray-900 mb-6 relative h-[300px]">
          {/* --- ВИБІР МОВИ (ПОВЕРХ ЗОБРАЖЕННЯ) --- */}
          <div className="absolute top-4 right-4 z-30" ref={menuRef}>
            <button
              onClick={() => setIsLanguageMenuOpen((prev) => !prev)}
              className="flex items-center space-x-2 bg-black/40 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5 hover:bg-black/60 transition-colors shadow-lg"
            >
              {languages
                .filter((lang) => lang.code === selectedLanguage)
                .map(({ Flag, alt }) => (
                  <Flag
                    key={alt}
                    className="w-6 h-6 rounded-sm object-cover"
                    title={alt}
                  />
                ))}
              <FaAngleDown className="w-3.5 h-3.5 text-white/80" />
            </button>

            {isLanguageMenuOpen && (
              <div className="absolute right-0 mt-2 bg-[#0c1218] border border-white/10 shadow-2xl rounded-xl w-36 p-1.5 space-y-1 z-40 overflow-hidden">
                {languages.map(({ code, Flag, alt }) => (
                  <button
                    key={code}
                    onClick={() => handleLanguageChange(code)}
                    className={`flex items-center space-x-3 w-full px-3 py-2 rounded-lg transition-colors ${
                      selectedLanguage === code
                        ? "bg-white/10"
                        : "hover:bg-white/5"
                    }`}
                  >
                    <Flag
                      className="w-5 h-5 rounded-sm object-cover"
                      title={alt}
                    />
                    <span className="text-sm font-medium">{alt}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* САМЕ ФОТО АБО ЗАГЛУШКА */}
          {tg.initDataUnsafe?.user?.photo_url ? (
            <>
              <img
                src={tg.initDataUnsafe?.user?.photo_url}
                className="absolute inset-0 w-full h-full object-cover"
                alt="Profile"
              />
              <div className="absolute bottom-0 left-0 right-0 z-10 px-5 py-6 bg-gradient-to-t from-gray-950 via-gray-950/80 to-transparent">
                <p className="text-2xl font-bold text-white drop-shadow-md">
                  {tg.initDataUnsafe?.user?.first_name
                    ? `${tg.initDataUnsafe?.user?.first_name} ${tg.initDataUnsafe?.user?.last_name || ""}`
                    : ""}
                </p>
                <p className="text-white/70 font-medium">
                  {tg.initDataUnsafe?.user?.username
                    ? "@" + tg.initDataUnsafe?.user?.username
                    : ""}
                </p>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 mt-auto">
              <div className="w-24 h-24 bg-gray-800 rounded-full flex items-center justify-center mb-4 border border-white/5 shadow-inner">
                <FaUser className="text-4xl text-gray-500" />
              </div>
              <p className="text-2xl font-bold text-white drop-shadow-md">
                {tg.initDataUnsafe?.user?.first_name
                  ? `${tg.initDataUnsafe?.user?.first_name} ${tg.initDataUnsafe?.user?.last_name || ""}`
                  : "Користувач"}
              </p>
            </div>
          )}
        </div>

        {/* ДАНІ КОРИСТУВАЧА */}
        <div className="text-start px-5 pb-8">
          {/* НОВА КАРТКА ДАТИ РЕЄСТРАЦІЇ */}
          {regDate && (
            <div className="flex items-center gap-4 bg-gray-900/80 p-4 rounded-2xl border border-white/5 mb-8 shadow-sm">
              <div className="w-12 h-12 flex-shrink-0 rounded-full bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                <FaCalendarCheck className="text-xl text-emerald-400" />
              </div>
              <div>
                <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-0.5">
                  {t("regDate", { defaultValue: "Дата реєстрації" })}
                </p>
                <p className="text-white font-medium text-base">
                  {formatDate(regDate)}
                </p>
              </div>
            </div>
          )}

          {/* АЧІВКИ */}
          <div className="mb-4">
            {achievements && (
              <div>
                <p className="text-xl font-bold text-white mb-4">
                  {t("achievements")}
                </p>
                <div className="overflow-x-auto snap-x snap-proximity flex space-x-4 pb-4 -mx-5 px-5 custom-scrollbar hidden-scrollbar">
                  {Object.entries(achievements)
                    .sort((a, b) => {
                      const numA = parseInt(a[0].replace("day", ""), 10);
                      const numB = parseInt(b[0].replace("day", ""), 10);
                      return numA - numB;
                    })
                    .map(([key, achieved]) => (
                      <div
                        key={key}
                        className="w-40 flex-shrink-0 snap-center flex flex-col items-center bg-gray-900 border border-white/5 rounded-2xl p-4"
                      >
                        <img
                          src={`/achievement_${key}.webp`}
                          className={`w-32 h-32 object-contain mb-3 drop-shadow-xl transition-all ${
                            !achieved
                              ? "filter grayscale brightness-50 opacity-60"
                              : ""
                          }`}
                          alt={key}
                        />
                        <p
                          className={`font-bold text-center ${achieved ? "text-emerald-400" : "text-gray-500"}`}
                        >
                          {t("dayStreak", { count: key.replace("day", "") })}
                        </p>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Ховаємо скроллбар для каруселі ачівок */}
      <style>{`
        .hidden-scrollbar::-webkit-scrollbar { display: none; }
        .hidden-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}

export default Profile;
